import { Component } from 'react';
import { FormGroup, Intent, NumericInput, Radio, RadioGroup, Switch } from '@blueprintjs/core';
import { Button, ListBox, ListBoxItem, Popover, Select } from '@launchpad-ui/components';
import { Icon as LaunchpadIcon } from '@launchpad-ui/icons';
import { Formik, type FormikProps } from 'formik';
import { clone, get, set, unset } from 'lodash';

import { type Scopes } from '../../auth';
import { type Settings } from '../../settings';
import { Alert, createBooleanChangeHandler, DefaultCallout } from '../../shared';
import {
  type AccountMetrics,
  BillingInterval,
  developer2025DefaultLimits,
  Foundation2023PlanVersion,
  Foundation2023PlanVersionReadableNames,
  foundation2025DefaultLimits,
  hasBillingInterval,
  isEnterprisePlan,
  isFixedPlan,
  isGuardianPlan,
  isHostBasedPlan,
  isPlanWithAIRuns,
  isPlanWithExperimentationMAU,
  isPlanWithObservabilityProducts,
  isPlanWithSSO,
  isPlanWithVegaAI,
  isSelfServePlan,
  MetricType,
  type Plan,
  PlanType,
  PlanTypeReadableNames,
  type Subscription,
  supportsFreeToggle,
  supportsPlanVersionSelection,
  TrialFeatures,
  UNLIMITED,
} from '../model';

import './PlanForm.scss';

// Plan types - matching the EXACT original Blueprint implementation items array
export const getAvailablePlanTypes = (isEnterpriseCreator: boolean, settings: Settings, currentPlanType?: PlanType) => {
  const basePlans = isEnterpriseCreator
    ? [
        PlanType.ENTERPRISE,
        PlanType.ENTERPRISE_2023,
        PlanType.ENTERPRISE_TRIAL,
        PlanType.FOUNDATION_2023,
        PlanType.FOUNDATION_TRIAL_2023,
        PlanType.GUARDIAN_2024,
        PlanType.GUARDIAN_SEATS_2024,
        PlanType.GUARDIAN_TRIAL_2024,
        PlanType.PROFESSIONAL,
        PlanType.PROFESSIONAL_2021,
        PlanType.STANDARD_TRIAL,
        PlanType.STANDARD_TRIAL_2021,
        PlanType.STARTER,
        PlanType.STARTER_2021,
      ]
    : [
        PlanType.ENTERPRISE_TRIAL,
        PlanType.FOUNDATION_TRIAL_2023,
        PlanType.GUARDIAN_TRIAL_2024,
        PlanType.STANDARD_TRIAL_2021,
      ];

  // Add Developer2025 if flag enabled OR already assigned
  if (settings.enableSelfServeDeveloper2025PlanSelection || currentPlanType === PlanType.DEVELOPER_2025) {
    basePlans.push(PlanType.DEVELOPER_2025);
  }

  // Add Foundation2025 if flag enabled OR already assigned
  if (settings.enableSelfServeFoundation2025PlanSelection || currentPlanType === PlanType.FOUNDATION_2025) {
    basePlans.push(PlanType.FOUNDATION_2025);
  }

  return basePlans.sort();
};

// Helper function to ensure values conform to NumericInput constraints
const normalizeValue = (value: number | undefined, min: number, stepSize: number): number => {
  if (value === undefined || value === null) {
    return min;
  }

  // Handle unlimited (-1) case
  if (value === UNLIMITED) {
    return UNLIMITED;
  }

  // Ensure value is at least the minimum
  if (value < min) {
    return min;
  }

  // Round to nearest step
  const steps = Math.round((value - min) / stepSize);
  return min + steps * stepSize;
};

export type PlanFormProps = {
  settings: Settings;
  scopes: Scopes;
  subscription: Subscription;
  isSubmitting: boolean;
  onSubmit: (values: Plan) => void;
};

type PlanFormState = Readonly<{
  isConfirmationOpen: boolean;
  enableEventsReceived: boolean;
  enableEventsPublished: boolean;
  enableExperimentationKeys: boolean;
  enableExperimentationMAU: boolean;
  enableAIServiceConnections: boolean;
  enableAnalyticsMAU: boolean;
  enableErrors: boolean;
  enableLogs: boolean;
  enableSessions: boolean;
  enableTraces: boolean;
  enableAIRuns: boolean;
  enableVegaAI: boolean;
}>;

const stepSizes: AccountMetrics = {
  [MetricType.SEATS]: 5,
  [MetricType.SERVER_CONNECTIONS]: 25,
  [MetricType.MONTHLY_ACTIVE_USERS]: 25000,
  [MetricType.EVENTS_RECEIVED]: 1000000,
  [MetricType.EVENTS_PUBLISHED]: 1000000,
  [MetricType.EXPERIMENTATION_KEYS]: 1000000,
  [MetricType.EXPERIMENTATION_MAU]: 1000000,
  [MetricType.SSO]: 1,
  [MetricType.HOSTS]: 1,
  [MetricType.AI_SERVICE_CONNECTIONS]: 1,
  [MetricType.ANALYTICS_MAU]: 1,
  [MetricType.ERRORS]: 1,
  [MetricType.LOGS]: 1,
  [MetricType.SESSIONS]: 1,
  [MetricType.TRACES]: 1,
  [MetricType.AI_RUNS]: 1,
  [MetricType.VEGA_AI]: 1,
};

export class PlanForm extends Component<PlanFormProps, PlanFormState> {
  public state = {
    isConfirmationOpen: false,
    enableEventsReceived: !!this.props.subscription._limits[MetricType.EVENTS_RECEIVED],
    enableEventsPublished: !!this.props.subscription._limits[MetricType.EVENTS_PUBLISHED],
    enableExperimentationKeys: !!this.props.subscription._limits[MetricType.EXPERIMENTATION_KEYS],
    enableExperimentationMAU: !!this.props.subscription._limits[MetricType.EXPERIMENTATION_MAU],
    enableAIServiceConnections: !!this.props.subscription._limits[MetricType.AI_SERVICE_CONNECTIONS],
    enableAnalyticsMAU: !!this.props.subscription._limits[MetricType.ANALYTICS_MAU],
    enableErrors: !!this.props.subscription._limits[MetricType.ERRORS],
    enableLogs: !!this.props.subscription._limits[MetricType.LOGS],
    enableSessions: !!this.props.subscription._limits[MetricType.SESSIONS],
    enableTraces: !!this.props.subscription._limits[MetricType.TRACES],
    enableAIRuns: !!this.props.subscription._limits[MetricType.AI_RUNS],
    enableVegaAI: !!this.props.subscription._limits[MetricType.VEGA_AI],
  };

  public handleEnableEventsReceivedChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableEventsReceived: checked })),
  );

  public handleEnableEventsPublishedChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableEventsPublished: checked })),
  );

  public handleEnableExperimentationKeysChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableExperimentationKeys: checked })),
  );

  public handleEnableExperimentationMauChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableExperimentationMAU: checked })),
  );

  public handleEnableAIServiceConnectionsChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableAIServiceConnections: checked })),
  );

  public handleEnableAnalyticsMAUChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableAnalyticsMAU: checked })),
  );

  public handleEnableErrorsChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableErrors: checked })),
  );

  public handleEnableLogsChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableLogs: checked })),
  );

  public handleEnableSessionsChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableSessions: checked })),
  );

  public handleEnableTracesChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableTraces: checked })),
  );

  public handleEnableAIRunsChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableAIRuns: checked })),
  );

  public handleEnableVegaAIChange = createBooleanChangeHandler((checked) =>
    this.setState(() => ({ enableVegaAI: checked })),
  );

  private handlePlanTypeChange = (newPlanType: PlanType, setFieldValue: (field: string, value: unknown) => void) => {
    setFieldValue('planType', newPlanType);

    // The plan version is fully optional: clear it on any plan type change so a
    // stale selection isn't carried over. Leaving it unset means the backend
    // keeps the subscription's current version; it's only sent when the admin
    // intentionally picks a version from the dropdown.
    setFieldValue('version', undefined);

    // If switching to Developer2025, set the default limits
    // Note: Don't set free=true - backend detects Developer2025 via IsFreeDevPlan()
    if (newPlanType === PlanType.DEVELOPER_2025) {
      Object.entries(developer2025DefaultLimits).forEach(([metricType, value]) => {
        setFieldValue(`limits.${metricType}`, value);
      });
      setFieldValue('billingInterval', BillingInterval.MONTHLY_BILLING);
      setFieldValue('free', false);
    } else if (newPlanType === PlanType.FOUNDATION_2025) {
      // Set reasonable default limits for Foundation2025
      Object.entries(foundation2025DefaultLimits).forEach(([metricType, value]) => {
        setFieldValue(`limits.${metricType}`, value);
      });
      setFieldValue('free', false);
      // Dynamically set toggle states based on which metrics have default limits
      this.setState({
        enableEventsReceived: MetricType.EVENTS_RECEIVED in foundation2025DefaultLimits,
        enableEventsPublished: MetricType.EVENTS_PUBLISHED in foundation2025DefaultLimits,
        enableExperimentationKeys: MetricType.EXPERIMENTATION_KEYS in foundation2025DefaultLimits,
        enableExperimentationMAU: MetricType.EXPERIMENTATION_MAU in foundation2025DefaultLimits,
        enableAIServiceConnections: MetricType.AI_SERVICE_CONNECTIONS in foundation2025DefaultLimits,
        enableAnalyticsMAU: MetricType.ANALYTICS_MAU in foundation2025DefaultLimits,
        enableErrors: MetricType.ERRORS in foundation2025DefaultLimits,
        enableLogs: MetricType.LOGS in foundation2025DefaultLimits,
        enableSessions: MetricType.SESSIONS in foundation2025DefaultLimits,
        enableTraces: MetricType.TRACES in foundation2025DefaultLimits,
        enableAIRuns: MetricType.AI_RUNS in foundation2025DefaultLimits,
        enableVegaAI: MetricType.VEGA_AI in foundation2025DefaultLimits,
      });
    } else {
      setFieldValue('free', false);
    }
  };

  public render() {
    const { subscription: sub, settings, scopes } = this.props;
    if (
      !scopes.enterprisePlanCreator &&
      sub.planType !== PlanType.STANDARD_TRIAL &&
      sub.planType !== PlanType.ENTERPRISE_TRIAL &&
      sub.planType !== PlanType.STANDARD_TRIAL_2021 &&
      sub.planType !== PlanType.FOUNDATION_TRIAL_2023 &&
      sub.planType !== PlanType.GUARDIAN_TRIAL_2024
    ) {
      return <DefaultCallout>Customer has a subscription and cannot be put on a trial</DefaultCallout>;
    }
    // Initialize form with backend-evaluated limits from sub._limits
    // This works for ALL plans including existing Developer2025 accounts
    // When user switches TO Developer2025, handlePlanTypeChange() overwrites these with developer2025DefaultLimits
    const initialLimits = sub._limits;
    const initialBillingInterval = (sub.billingInterval as BillingInterval) || BillingInterval.MONTHLY_BILLING;

    return (
      <Formik
        initialValues={{
          planType: sub.planType,
          // Plan version is fully optional and starts unset. Omitting it on submit
          // lets the backend keep the subscription's current version; it's only sent
          // when the admin intentionally picks a version from the dropdown.
          version: undefined,
          free: false,
          featureTrial: TrialFeatures.EXPERIMENTATION_KEY,
          effectiveStartDate: new Date().getTime(),
          prorationTime: Math.round(new Date().getTime() / 1000),
          // @ts-expect-error -- Removing this opens a can of worms with the types for Formik that I've attempted to fix, but it's causing cascading issues that aren't worth the time needed to fix
          limits: {
            [MetricType.SEATS]: normalizeValue(
              initialLimits[MetricType.SEATS],
              stepSizes[MetricType.SEATS],
              stepSizes[MetricType.SEATS],
            ),
            [MetricType.MONTHLY_ACTIVE_USERS]: normalizeValue(
              initialLimits[MetricType.MONTHLY_ACTIVE_USERS],
              stepSizes[MetricType.MONTHLY_ACTIVE_USERS],
              stepSizes[MetricType.MONTHLY_ACTIVE_USERS],
            ),
            [MetricType.SERVER_CONNECTIONS]: normalizeValue(
              initialLimits[MetricType.SERVER_CONNECTIONS],
              stepSizes[MetricType.SERVER_CONNECTIONS],
              stepSizes[MetricType.SERVER_CONNECTIONS],
            ),
            [MetricType.EVENTS_RECEIVED]: normalizeValue(
              initialLimits[MetricType.EVENTS_RECEIVED],
              stepSizes[MetricType.EVENTS_RECEIVED],
              stepSizes[MetricType.EVENTS_RECEIVED],
            ),
            [MetricType.EVENTS_PUBLISHED]: normalizeValue(
              initialLimits[MetricType.EVENTS_PUBLISHED],
              stepSizes[MetricType.EVENTS_PUBLISHED],
              stepSizes[MetricType.EVENTS_PUBLISHED],
            ),
            [MetricType.EXPERIMENTATION_KEYS]: normalizeValue(
              initialLimits[MetricType.EXPERIMENTATION_KEYS],
              stepSizes[MetricType.EXPERIMENTATION_KEYS],
              stepSizes[MetricType.EXPERIMENTATION_KEYS],
            ),
            [MetricType.EXPERIMENTATION_MAU]: normalizeValue(
              initialLimits[MetricType.EXPERIMENTATION_MAU],
              stepSizes[MetricType.EXPERIMENTATION_MAU],
              stepSizes[MetricType.EXPERIMENTATION_MAU],
            ),
            [MetricType.SSO]: initialLimits[MetricType.SSO] || 0,
            [MetricType.HOSTS]: normalizeValue(
              initialLimits[MetricType.HOSTS],
              stepSizes[MetricType.HOSTS],
              stepSizes[MetricType.HOSTS],
            ),
            [MetricType.AI_SERVICE_CONNECTIONS]: normalizeValue(
              initialLimits[MetricType.AI_SERVICE_CONNECTIONS],
              stepSizes[MetricType.AI_SERVICE_CONNECTIONS],
              stepSizes[MetricType.AI_SERVICE_CONNECTIONS],
            ),
            [MetricType.ANALYTICS_MAU]: normalizeValue(
              initialLimits[MetricType.ANALYTICS_MAU],
              stepSizes[MetricType.ANALYTICS_MAU],
              stepSizes[MetricType.ANALYTICS_MAU],
            ),
            [MetricType.ERRORS]: normalizeValue(
              initialLimits[MetricType.ERRORS],
              stepSizes[MetricType.ERRORS],
              stepSizes[MetricType.ERRORS],
            ),
            [MetricType.LOGS]: normalizeValue(
              initialLimits[MetricType.LOGS],
              stepSizes[MetricType.LOGS],
              stepSizes[MetricType.LOGS],
            ),
            [MetricType.SESSIONS]: normalizeValue(
              initialLimits[MetricType.SESSIONS],
              stepSizes[MetricType.SESSIONS],
              stepSizes[MetricType.SESSIONS],
            ),
            [MetricType.TRACES]: normalizeValue(
              initialLimits[MetricType.TRACES],
              stepSizes[MetricType.TRACES],
              stepSizes[MetricType.TRACES],
            ),
            [MetricType.AI_RUNS]: normalizeValue(
              initialLimits[MetricType.AI_RUNS],
              stepSizes[MetricType.AI_RUNS],
              stepSizes[MetricType.AI_RUNS],
            ),
            [MetricType.VEGA_AI]: normalizeValue(
              initialLimits[MetricType.VEGA_AI],
              stepSizes[MetricType.VEGA_AI],
              stepSizes[MetricType.VEGA_AI],
            ),
          },
          billingInterval: initialBillingInterval,
        }}
        validate={(values: Plan) => this.handleValidation(values, settings)}
        onSubmit={() => this.setState({ isConfirmationOpen: true })}
        render={(form: FormikProps<Plan>) => (
          <form onSubmit={form.handleSubmit}>
            <FormGroup label="Plan type" labelInfo="(required)" helperText={this.getFieldHelperText(form, 'planType')}>
              <Select
                value={form.values.planType}
                onChange={(key) => this.handlePlanTypeChange(key as PlanType, form.setFieldValue)}
                className="PlanForm-select"
                aria-label="Plan type selection"
              >
                <Button>
                  <LaunchpadIcon name="package" slot="start-icon" />
                  <span>{PlanTypeReadableNames[form.values.planType]}</span>
                  <LaunchpadIcon name="chevron-down" slot="end-icon" />
                </Button>
                <Popover>
                  <ListBox>
                    {getAvailablePlanTypes(scopes.enterprisePlanCreator, settings, sub.planType).map((planType) => (
                      <ListBoxItem key={planType} id={planType}>
                        {PlanTypeReadableNames[planType]}
                      </ListBoxItem>
                    ))}
                  </ListBox>
                </Popover>
              </Select>
            </FormGroup>
            {supportsPlanVersionSelection(form.values.planType) && (
              <FormGroup label="Plan version" helperText="The product flavor for this Foundation 2023 plan">
                <Select
                  value={form.values.version ?? ''}
                  onChange={(key) => form.setFieldValue('version', key ? Number(key) : undefined)}
                  className="PlanForm-select"
                  aria-label="Plan version selection"
                >
                  <Button>
                    <LaunchpadIcon name="package" slot="start-icon" />
                    <span>
                      {form.values.version
                        ? Foundation2023PlanVersionReadableNames[form.values.version]
                        : 'Select plan version'}
                    </span>
                    <LaunchpadIcon name="chevron-down" slot="end-icon" />
                  </Button>
                  <Popover>
                    <ListBox>
                      {Object.values(Foundation2023PlanVersion)
                        .filter((value): value is Foundation2023PlanVersion => typeof value === 'number')
                        .map((version) => (
                          <ListBoxItem key={version} id={version}>
                            {Foundation2023PlanVersionReadableNames[version]}
                          </ListBoxItem>
                        ))}
                    </ListBox>
                  </Popover>
                </Select>
              </FormGroup>
            )}
            {hasBillingInterval(form.values.planType) && (
              <FormGroup helperText="Whether this plan should be billed monthly or annually">
                <RadioGroup
                  name="interval"
                  onChange={(event) => {
                    const newInterval = event.currentTarget.value as unknown as BillingInterval;
                    form.setFieldValue('billingInterval', newInterval);
                  }}
                  selectedValue={form.values.billingInterval}
                >
                  <Radio labelElement={<strong>Monthly</strong>} value={BillingInterval.MONTHLY_BILLING} />
                  <Radio labelElement={<strong>Annual</strong>} value={BillingInterval.ANNUAL_BILLING} />
                </RadioGroup>
              </FormGroup>
            )}
            {supportsFreeToggle(form.values.planType) && (
              <FormGroup helperText="A free plan does not create a corresponding subscription in stripe, so the customer can keep it indefinitely for free.">
                <Switch
                  id="free"
                  name="free"
                  labelElement={<strong>This is a free plan</strong>}
                  checked={form.values.free}
                  onChange={(event) => form.setFieldValue('free', event.currentTarget.checked)}
                />
              </FormGroup>
            )}

            {!isFixedPlan(form.values.planType) && !this.isMonthlyFoundation2023(form.values) && (
              <>
                {!isHostBasedPlan(form.values.planType) && (
                  <FormGroup
                    label="Number of seats"
                    labelFor="seats"
                    labelInfo="(required)"
                    helperText={this.getFieldHelperText(form, `limits.${MetricType.SEATS}`)}
                    intent={this.getFieldIntent(form, `limits.${MetricType.SEATS}`)}
                  >
                    <NumericInput
                      min={stepSizes[MetricType.SEATS]}
                      stepSize={stepSizes[MetricType.SEATS]}
                      majorStepSize={2 * stepSizes[MetricType.SEATS]}
                      id="seats"
                      name={`limits.${MetricType.SEATS}`}
                      value={this.getNumericInputValue(form.values.limits[MetricType.SEATS])}
                      placeholder={form.values.limits[MetricType.SEATS] === UNLIMITED ? 'Unlimited' : undefined}
                      intent={this.getFieldIntent(form, `limits.${MetricType.SEATS}`)}
                      onBlur={form.handleBlur}
                      onValueChange={(value) => form.setFieldValue(`limits.${MetricType.SEATS}`, value)}
                      disabled={form.values.limits[MetricType.SEATS] === UNLIMITED}
                    />
                    <Switch
                      id="unlimited_seats"
                      name="unlimited_seats"
                      checked={form.values.limits[MetricType.SEATS] === UNLIMITED}
                      labelElement={<strong>Unlimited seats</strong>}
                      onChange={(event) => {
                        if (event.currentTarget.checked) {
                          form.setFieldValue(`limits.${MetricType.SEATS}`, UNLIMITED);
                          return;
                        }
                        form.setFieldValue(`limits.${MetricType.SEATS}`, stepSizes[MetricType.SEATS]);
                      }}
                    />
                  </FormGroup>
                )}
                {isHostBasedPlan(form.values.planType) && (
                  <FormGroup
                    label="Number of hosts"
                    labelFor="hosts"
                    labelInfo="(required)"
                    helperText={this.getFieldHelperText(form, `limits.${MetricType.HOSTS}`)}
                    intent={this.getFieldIntent(form, `limits.${MetricType.HOSTS}`)}
                  >
                    <NumericInput
                      min={0}
                      stepSize={stepSizes[MetricType.HOSTS]}
                      majorStepSize={2 * stepSizes[MetricType.HOSTS]}
                      id="hosts"
                      name={`limits.${MetricType.HOSTS}`}
                      value={this.getNumericInputValue(form.values.limits[MetricType.HOSTS])}
                      placeholder={form.values.limits[MetricType.HOSTS] === UNLIMITED ? 'Unlimited' : undefined}
                      intent={this.getFieldIntent(form, `limits.${MetricType.HOSTS}`)}
                      onBlur={form.handleBlur}
                      onValueChange={(value) => form.setFieldValue(`limits.${MetricType.HOSTS}`, value)}
                      disabled={form.values.limits[MetricType.HOSTS] === UNLIMITED}
                    />
                  </FormGroup>
                )}

                {!settings.removeServerConnections && (
                  <FormGroup
                    label="Number of server connections"
                    labelFor="server-connections"
                    labelInfo="(required)"
                    helperText={this.getFieldHelperText(form, `limits.${MetricType.SERVER_CONNECTIONS}`)}
                    intent={this.getFieldIntent(form, `limits.${MetricType.SERVER_CONNECTIONS}`)}
                  >
                    <NumericInput
                      min={stepSizes[MetricType.SERVER_CONNECTIONS]}
                      stepSize={stepSizes[MetricType.SERVER_CONNECTIONS]}
                      majorStepSize={2 * stepSizes[MetricType.SERVER_CONNECTIONS]}
                      id="server-connections"
                      name={`limits.${MetricType.SERVER_CONNECTIONS}`}
                      value={form.values.limits[MetricType.SERVER_CONNECTIONS]}
                      intent={this.getFieldIntent(form, `limits.${MetricType.SERVER_CONNECTIONS}`)}
                      onBlur={form.handleBlur}
                      onValueChange={(value) => form.setFieldValue(`limits.${MetricType.SERVER_CONNECTIONS}`, value)}
                    />
                  </FormGroup>
                )}

                <FormGroup
                  label="Number of MAUs"
                  labelFor="monthly-active-users"
                  helperText={this.getFieldHelperText(form, `limits.${MetricType.MONTHLY_ACTIVE_USERS}`)}
                  intent={this.getFieldIntent(form, `limits.${MetricType.MONTHLY_ACTIVE_USERS}`)}
                >
                  <NumericInput
                    min={stepSizes[MetricType.MONTHLY_ACTIVE_USERS]}
                    stepSize={stepSizes[MetricType.MONTHLY_ACTIVE_USERS]}
                    majorStepSize={2 * stepSizes[MetricType.MONTHLY_ACTIVE_USERS]}
                    id="monthly-active-users"
                    name={`limits.${MetricType.MONTHLY_ACTIVE_USERS}`}
                    value={this.getNumericInputValue(form.values.limits[MetricType.MONTHLY_ACTIVE_USERS])}
                    placeholder={
                      form.values.limits[MetricType.MONTHLY_ACTIVE_USERS] === UNLIMITED ? 'Unlimited' : undefined
                    }
                    intent={this.getFieldIntent(form, `limits.${MetricType.MONTHLY_ACTIVE_USERS}`)}
                    onBlur={form.handleBlur}
                    onValueChange={(value) => form.setFieldValue(`limits.${MetricType.MONTHLY_ACTIVE_USERS}`, value)}
                    disabled={form.values.limits[MetricType.MONTHLY_ACTIVE_USERS] === UNLIMITED}
                  />
                  <Switch
                    id="unlimited_mau"
                    name="unlimited_mau"
                    checked={form.values.limits[MetricType.MONTHLY_ACTIVE_USERS] === UNLIMITED}
                    labelElement={<strong>Unlimited MAUs</strong>}
                    onChange={(event) => {
                      if (event.currentTarget.checked) {
                        form.setFieldValue(`limits.${MetricType.MONTHLY_ACTIVE_USERS}`, UNLIMITED);
                        return;
                      }
                      form.setFieldValue(
                        `limits.${MetricType.MONTHLY_ACTIVE_USERS}`,
                        stepSizes[MetricType.MONTHLY_ACTIVE_USERS],
                      );
                    }}
                  />
                </FormGroup>

                <Switch
                  labelElement={<strong>Include experiment events</strong>}
                  checked={this.state.enableEventsReceived}
                  onChange={this.handleEnableEventsReceivedChange}
                />
                {this.state.enableEventsReceived && (
                  <FormGroup
                    label="Number of experiment events"
                    labelFor="events-received"
                    labelInfo="(required)"
                    helperText={this.getFieldHelperText(form, `limits.${MetricType.EVENTS_RECEIVED}`)}
                    intent={this.getFieldIntent(form, `limits.${MetricType.EVENTS_RECEIVED}`)}
                  >
                    <NumericInput
                      min={UNLIMITED}
                      stepSize={stepSizes[MetricType.EVENTS_RECEIVED]}
                      majorStepSize={2 * stepSizes[MetricType.EVENTS_RECEIVED]}
                      id="events-received"
                      name={`limits.${MetricType.EVENTS_RECEIVED}`}
                      value={form.values.limits[MetricType.EVENTS_RECEIVED]}
                      intent={this.getFieldIntent(form, `limits.${MetricType.EVENTS_RECEIVED}`)}
                      onBlur={form.handleBlur}
                      onValueChange={(value) => form.setFieldValue(`limits.${MetricType.EVENTS_RECEIVED}`, value)}
                    />
                  </FormGroup>
                )}

                <Switch
                  labelElement={<strong>Include data export events</strong>}
                  checked={this.state.enableEventsPublished}
                  onChange={this.handleEnableEventsPublishedChange}
                />
                {this.state.enableEventsPublished && (
                  <FormGroup
                    label="Number of data export events"
                    labelFor="events-published"
                    labelInfo="(required)"
                    helperText={this.getFieldHelperText(form, `limits.${MetricType.EVENTS_PUBLISHED}`)}
                    intent={this.getFieldIntent(form, `limits.${MetricType.EVENTS_PUBLISHED}`)}
                  >
                    <NumericInput
                      min={stepSizes[MetricType.EVENTS_PUBLISHED]}
                      stepSize={stepSizes[MetricType.EVENTS_PUBLISHED]}
                      majorStepSize={2 * stepSizes[MetricType.EVENTS_PUBLISHED]}
                      id="events-published"
                      name={`limits.${MetricType.EVENTS_PUBLISHED}`}
                      value={form.values.limits[MetricType.EVENTS_PUBLISHED]}
                      intent={this.getFieldIntent(form, `limits.${MetricType.EVENTS_PUBLISHED}`)}
                      onBlur={form.handleBlur}
                      onValueChange={(value) => form.setFieldValue(`limits.${MetricType.EVENTS_PUBLISHED}`, value)}
                    />
                  </FormGroup>
                )}

                <Switch
                  labelElement={<strong>Include experiment keys</strong>}
                  checked={this.state.enableExperimentationKeys}
                  onChange={this.handleEnableExperimentationKeysChange}
                />
                {this.state.enableExperimentationKeys && (
                  <FormGroup
                    label="Number of experiment keys"
                    labelFor="experimentation-keys-received"
                    labelInfo="(required)"
                    helperText={this.getFieldHelperText(form, `limits.${MetricType.EXPERIMENTATION_KEYS}`)}
                    intent={this.getFieldIntent(form, `limits.${MetricType.EXPERIMENTATION_KEYS}`)}
                  >
                    <NumericInput
                      min={stepSizes[MetricType.EXPERIMENTATION_KEYS]}
                      stepSize={stepSizes[MetricType.EXPERIMENTATION_KEYS]}
                      majorStepSize={2 * stepSizes[MetricType.EXPERIMENTATION_KEYS]}
                      id="experimentation-keys"
                      name={`limits.${MetricType.EXPERIMENTATION_KEYS}`}
                      value={form.values.limits[MetricType.EXPERIMENTATION_KEYS]}
                      intent={this.getFieldIntent(form, `limits.${MetricType.EXPERIMENTATION_KEYS}`)}
                      onBlur={form.handleBlur}
                      onValueChange={(value) => form.setFieldValue(`limits.${MetricType.EXPERIMENTATION_KEYS}`, value)}
                    />
                  </FormGroup>
                )}
                <Switch
                  labelElement={<strong>Include Experimentation MAU</strong>}
                  checked={this.state.enableExperimentationMAU}
                  onChange={this.handleEnableExperimentationMauChange}
                />
                {this.state.enableExperimentationMAU && (
                  <FormGroup
                    label="Number of Experimentation MAU"
                    labelFor="experimentation-mau"
                    labelInfo="(required)"
                    helperText={this.getFieldHelperText(form, `limits.${MetricType.EXPERIMENTATION_MAU}`)}
                    intent={this.getFieldIntent(form, `limits.${MetricType.EXPERIMENTATION_MAU}`)}
                  >
                    <NumericInput
                      min={stepSizes[MetricType.EXPERIMENTATION_MAU]}
                      stepSize={stepSizes[MetricType.EXPERIMENTATION_MAU]}
                      majorStepSize={2 * stepSizes[MetricType.EXPERIMENTATION_MAU]}
                      id="experimentation-mau"
                      name={`limits.${MetricType.EXPERIMENTATION_MAU}`}
                      value={form.values.limits[MetricType.EXPERIMENTATION_MAU]}
                      intent={this.getFieldIntent(form, `limits.${MetricType.EXPERIMENTATION_MAU}`)}
                      onBlur={form.handleBlur}
                      onValueChange={(value) => form.setFieldValue(`limits.${MetricType.EXPERIMENTATION_MAU}`, value)}
                    />
                  </FormGroup>
                )}
                {isPlanWithObservabilityProducts(form.values.planType) && (
                  <>
                    <Switch
                      labelElement={<strong>Include AI Service Connections</strong>}
                      checked={this.state.enableAIServiceConnections}
                      onChange={this.handleEnableAIServiceConnectionsChange}
                    />
                    {this.state.enableAIServiceConnections && (
                      <FormGroup
                        label="Number of AI Service Connections"
                        labelFor="ai-service-connections"
                        labelInfo="(required)"
                        helperText={this.getFieldHelperText(form, `limits.${MetricType.AI_SERVICE_CONNECTIONS}`)}
                        intent={this.getFieldIntent(form, `limits.${MetricType.AI_SERVICE_CONNECTIONS}`)}
                      >
                        <NumericInput
                          min={stepSizes[MetricType.AI_SERVICE_CONNECTIONS]}
                          stepSize={stepSizes[MetricType.AI_SERVICE_CONNECTIONS]}
                          majorStepSize={2 * stepSizes[MetricType.AI_SERVICE_CONNECTIONS]}
                          id="ai-service-connections"
                          name={`limits.${MetricType.AI_SERVICE_CONNECTIONS}`}
                          value={form.values.limits[MetricType.AI_SERVICE_CONNECTIONS]}
                          intent={this.getFieldIntent(form, `limits.${MetricType.AI_SERVICE_CONNECTIONS}`)}
                          onBlur={form.handleBlur}
                          onValueChange={(value) =>
                            form.setFieldValue(`limits.${MetricType.AI_SERVICE_CONNECTIONS}`, value)
                          }
                        />
                      </FormGroup>
                    )}
                    <Switch
                      labelElement={<strong>Include Analytics MAU</strong>}
                      checked={this.state.enableAnalyticsMAU}
                      onChange={this.handleEnableAnalyticsMAUChange}
                    />
                    {this.state.enableAnalyticsMAU && (
                      <FormGroup
                        label="Number of Analytics MAU"
                        labelFor="analytics-mau"
                        labelInfo="(required)"
                        helperText={this.getFieldHelperText(form, `limits.${MetricType.ANALYTICS_MAU}`)}
                        intent={this.getFieldIntent(form, `limits.${MetricType.ANALYTICS_MAU}`)}
                      >
                        <NumericInput
                          min={stepSizes[MetricType.ANALYTICS_MAU]}
                          stepSize={stepSizes[MetricType.ANALYTICS_MAU]}
                          majorStepSize={2 * stepSizes[MetricType.ANALYTICS_MAU]}
                          id="analytics-mau"
                          name={`limits.${MetricType.ANALYTICS_MAU}`}
                          value={form.values.limits[MetricType.ANALYTICS_MAU]}
                          intent={this.getFieldIntent(form, `limits.${MetricType.ANALYTICS_MAU}`)}
                          onBlur={form.handleBlur}
                          onValueChange={(value) => form.setFieldValue(`limits.${MetricType.ANALYTICS_MAU}`, value)}
                        />
                      </FormGroup>
                    )}
                    <Switch
                      labelElement={<strong>Include Errors</strong>}
                      checked={this.state.enableErrors}
                      onChange={this.handleEnableErrorsChange}
                    />
                    {this.state.enableErrors && (
                      <FormGroup
                        label="Number of Errors"
                        labelFor="errors"
                        labelInfo="(required)"
                        helperText={this.getFieldHelperText(form, `limits.${MetricType.ERRORS}`)}
                        intent={this.getFieldIntent(form, `limits.${MetricType.ERRORS}`)}
                      >
                        <NumericInput
                          min={stepSizes[MetricType.ERRORS]}
                          stepSize={stepSizes[MetricType.ERRORS]}
                          majorStepSize={2 * stepSizes[MetricType.ERRORS]}
                          id="errors"
                          name={`limits.${MetricType.ERRORS}`}
                          value={form.values.limits[MetricType.ERRORS]}
                          intent={this.getFieldIntent(form, `limits.${MetricType.ERRORS}`)}
                          onBlur={form.handleBlur}
                          onValueChange={(value) => form.setFieldValue(`limits.${MetricType.ERRORS}`, value)}
                        />
                      </FormGroup>
                    )}
                    <Switch
                      labelElement={<strong>Include Logs</strong>}
                      checked={this.state.enableLogs}
                      onChange={this.handleEnableLogsChange}
                    />
                    {this.state.enableLogs && (
                      <FormGroup
                        label="Number of Logs"
                        labelFor="logs"
                        labelInfo="(required)"
                        helperText={this.getFieldHelperText(form, `limits.${MetricType.LOGS}`)}
                        intent={this.getFieldIntent(form, `limits.${MetricType.LOGS}`)}
                      >
                        <NumericInput
                          min={stepSizes[MetricType.LOGS]}
                          stepSize={stepSizes[MetricType.LOGS]}
                          majorStepSize={2 * stepSizes[MetricType.LOGS]}
                          id="logs"
                          name={`limits.${MetricType.LOGS}`}
                          value={form.values.limits[MetricType.LOGS]}
                          intent={this.getFieldIntent(form, `limits.${MetricType.LOGS}`)}
                          onBlur={form.handleBlur}
                          onValueChange={(value) => form.setFieldValue(`limits.${MetricType.LOGS}`, value)}
                        />
                      </FormGroup>
                    )}
                    <Switch
                      labelElement={<strong>Include Sessions</strong>}
                      checked={this.state.enableSessions}
                      onChange={this.handleEnableSessionsChange}
                    />
                    {this.state.enableSessions && (
                      <FormGroup
                        label="Number of Sessions"
                        labelFor="sessions"
                        labelInfo="(required)"
                        helperText={this.getFieldHelperText(form, `limits.${MetricType.SESSIONS}`)}
                        intent={this.getFieldIntent(form, `limits.${MetricType.SESSIONS}`)}
                      >
                        <NumericInput
                          min={stepSizes[MetricType.SESSIONS]}
                          stepSize={stepSizes[MetricType.SESSIONS]}
                          majorStepSize={2 * stepSizes[MetricType.SESSIONS]}
                          id="sessions"
                          name={`limits.${MetricType.SESSIONS}`}
                          value={form.values.limits[MetricType.SESSIONS]}
                          intent={this.getFieldIntent(form, `limits.${MetricType.SESSIONS}`)}
                          onBlur={form.handleBlur}
                          onValueChange={(value) => form.setFieldValue(`limits.${MetricType.SESSIONS}`, value)}
                        />
                      </FormGroup>
                    )}
                    <Switch
                      labelElement={<strong>Include Traces</strong>}
                      checked={this.state.enableTraces}
                      onChange={this.handleEnableTracesChange}
                    />
                    {this.state.enableTraces && (
                      <FormGroup
                        label="Number of Traces"
                        labelFor="traces"
                        labelInfo="(required)"
                        helperText={this.getFieldHelperText(form, `limits.${MetricType.TRACES}`)}
                        intent={this.getFieldIntent(form, `limits.${MetricType.TRACES}`)}
                      >
                        <NumericInput
                          min={stepSizes[MetricType.TRACES]}
                          stepSize={stepSizes[MetricType.TRACES]}
                          majorStepSize={2 * stepSizes[MetricType.TRACES]}
                          id="traces"
                          name={`limits.${MetricType.TRACES}`}
                          value={form.values.limits[MetricType.TRACES]}
                          intent={this.getFieldIntent(form, `limits.${MetricType.TRACES}`)}
                          onBlur={form.handleBlur}
                          onValueChange={(value) => form.setFieldValue(`limits.${MetricType.TRACES}`, value)}
                        />
                      </FormGroup>
                    )}
                  </>
                )}
                {isPlanWithVegaAI(form.values.planType) && (
                  <>
                    <Switch
                      labelElement={<strong>Include Vega AI</strong>}
                      checked={this.state.enableVegaAI}
                      onChange={this.handleEnableVegaAIChange}
                    />
                    {this.state.enableVegaAI && (
                      <FormGroup
                        label="Number of Vega AI Prompts"
                        labelFor="vega-ai"
                        labelInfo="(required)"
                        helperText={this.getFieldHelperText(form, `limits.${MetricType.VEGA_AI}`)}
                        intent={this.getFieldIntent(form, `limits.${MetricType.VEGA_AI}`)}
                      >
                        <NumericInput
                          min={stepSizes[MetricType.VEGA_AI]}
                          stepSize={stepSizes[MetricType.VEGA_AI]}
                          majorStepSize={2 * stepSizes[MetricType.VEGA_AI]}
                          id="vega-ai"
                          name={`limits.${MetricType.VEGA_AI}`}
                          value={form.values.limits[MetricType.VEGA_AI]}
                          intent={this.getFieldIntent(form, `limits.${MetricType.VEGA_AI}`)}
                          onBlur={form.handleBlur}
                          onValueChange={(value) => form.setFieldValue(`limits.${MetricType.VEGA_AI}`, value)}
                        />
                      </FormGroup>
                    )}
                  </>
                )}
                {isPlanWithAIRuns(form.values.planType) && (
                  <>
                    <Switch
                      labelElement={<strong>Include AI Runs</strong>}
                      checked={this.state.enableAIRuns}
                      onChange={this.handleEnableAIRunsChange}
                    />
                    {this.state.enableAIRuns && (
                      <FormGroup
                        label="Number of AI Runs"
                        labelFor="ai-runs"
                        labelInfo="(required)"
                        helperText={this.getFieldHelperText(form, `limits.${MetricType.AI_RUNS}`)}
                        intent={this.getFieldIntent(form, `limits.${MetricType.AI_RUNS}`)}
                      >
                        <NumericInput
                          min={stepSizes[MetricType.AI_RUNS]}
                          stepSize={stepSizes[MetricType.AI_RUNS]}
                          majorStepSize={2 * stepSizes[MetricType.AI_RUNS]}
                          id="ai-runs"
                          name={`limits.${MetricType.AI_RUNS}`}
                          value={form.values.limits[MetricType.AI_RUNS]}
                          intent={this.getFieldIntent(form, `limits.${MetricType.AI_RUNS}`)}
                          onBlur={form.handleBlur}
                          onValueChange={(value) => form.setFieldValue(`limits.${MetricType.AI_RUNS}`, value)}
                        />
                      </FormGroup>
                    )}
                  </>
                )}
              </>
            )}
            {isPlanWithSSO(form.values.planType) && (
              <Switch
                labelElement={<strong>Enable SSO</strong>}
                checked={!!form.values.limits[MetricType.SSO]}
                onChange={(event) =>
                  form.setFieldValue(`limits.${MetricType.SSO}`, event.currentTarget.checked ? 1 : 0)
                }
              />
            )}
            {isSelfServePlan(form.values.planType) &&
              !form.values.free &&
              form.values.planType !== PlanType.DEVELOPER_2025 && (
                <FormGroup
                  label="Proration time"
                  labelFor="proration-time"
                  labelInfo="(required)"
                  helperText="When to do the proration, if any is going to happen, in unix time (default is now)"
                >
                  <NumericInput
                    min={0}
                    id="proration-time"
                    name="prorationTime"
                    value={form.values.prorationTime}
                    onBlur={form.handleBlur}
                    onValueChange={(value) => form.setFieldValue('prorationTime', value)}
                  />
                </FormGroup>
              )}

            {(isEnterprisePlan(form.values.planType) || isGuardianPlan(form.values.planType) || form.values.free) && (
              <FormGroup
                label="Effective start date"
                labelFor="effective-start-date"
                labelInfo="(required)"
                helperText="When we should record the plan as having started, in unix millis (default is now)"
              >
                <NumericInput
                  min={0}
                  id="effective-start-date"
                  name="effectiveStartDate"
                  value={form.values.effectiveStartDate}
                  onBlur={form.handleBlur}
                  onValueChange={(value) => form.setFieldValue('effectiveStartDate', value)}
                />
              </FormGroup>
            )}
            <FormGroup>
              <Button variant="primary" type="submit" isDisabled={this.props.isSubmitting}>
                {this.props.isSubmitting ? 'Saving plan…' : 'Save plan'}
              </Button>
              <Alert
                isOpen={this.state.isConfirmationOpen}
                onClose={() => this.setState({ isConfirmationOpen: false })}
                onConfirm={() => this.handleSubmit(form.values)}
                onCancel={() => this.setState({ isConfirmationOpen: false })}
                confirmButtonText="Confirm"
                cancelButtonText="Cancel"
                intent="danger"
                title="Confirm Plan Change"
              >
                Are you sure you want to change this plan?
              </Alert>
            </FormGroup>
          </form>
        )}
      />
    );
  }

  public handleValidation = (values: Plan, settings: Settings) => {
    const {
      enableEventsReceived,
      enableEventsPublished,
      enableExperimentationKeys,
      enableExperimentationMAU,
      enableAIServiceConnections,
      enableAnalyticsMAU,
      enableErrors,
      enableLogs,
      enableSessions,
      enableTraces,
      enableAIRuns,
      enableVegaAI,
    } = this.state;
    const errors = {};

    if (!isFixedPlan(values.planType) && !this.isMonthlyFoundation2023(values)) {
      if (!values.limits[MetricType.SEATS] && !isHostBasedPlan(values.planType)) {
        set(errors, `limits.${MetricType.SEATS}`, 'Number of seats is required');
      }
      if (!values.limits[MetricType.HOSTS] && isHostBasedPlan(values.planType)) {
        set(errors, `limits.${MetricType.HOSTS}`, 'Number of hosts is required');
      }

      if (!settings.removeServerConnections && !values.limits[MetricType.SERVER_CONNECTIONS]) {
        set(errors, `limits.${MetricType.SERVER_CONNECTIONS}`, 'Number of server connections is required');
      }

      if (enableEventsReceived && typeof values.limits[MetricType.EVENTS_RECEIVED] !== 'number') {
        set(errors, `limits.${MetricType.EVENTS_RECEIVED}`, 'Number of events received is required');
      }

      if (enableEventsPublished && typeof values.limits[MetricType.EVENTS_PUBLISHED] !== 'number') {
        set(errors, `limits.${MetricType.EVENTS_PUBLISHED}`, 'Number of events published is required');
      }

      if (enableExperimentationKeys && typeof values.limits[MetricType.EXPERIMENTATION_KEYS] !== 'number') {
        set(errors, `limits.${MetricType.EXPERIMENTATION_KEYS}`, 'Number of Experimentation keys is required');
      }

      if (enableExperimentationMAU && typeof values.limits[MetricType.EXPERIMENTATION_MAU] !== 'number') {
        set(errors, `limits.${MetricType.EXPERIMENTATION_MAU}`, 'Number of Experimentation MAU is required');
      }

      if (isPlanWithObservabilityProducts(values.planType)) {
        if (enableAIServiceConnections && typeof values.limits[MetricType.AI_SERVICE_CONNECTIONS] !== 'number') {
          set(errors, `limits.${MetricType.AI_SERVICE_CONNECTIONS}`, 'Number of AI Service Connections is required');
        }
        if (enableAnalyticsMAU && typeof values.limits[MetricType.ANALYTICS_MAU] !== 'number') {
          set(errors, `limits.${MetricType.ANALYTICS_MAU}`, 'Number of Analytics MAU is required');
        }
        if (enableErrors && typeof values.limits[MetricType.ERRORS] !== 'number') {
          set(errors, `limits.${MetricType.ERRORS}`, 'Number of Errors is required');
        }
        if (enableLogs && typeof values.limits[MetricType.LOGS] !== 'number') {
          set(errors, `limits.${MetricType.LOGS}`, 'Number of Logs is required');
        }
        if (enableSessions && typeof values.limits[MetricType.SESSIONS] !== 'number') {
          set(errors, `limits.${MetricType.SESSIONS}`, 'Number of Sessions is required');
        }
        if (enableTraces && typeof values.limits[MetricType.TRACES] !== 'number') {
          set(errors, `limits.${MetricType.TRACES}`, 'Number of Traces is required');
        }
      }

      if (isPlanWithVegaAI(values.planType)) {
        if (enableVegaAI && typeof values.limits[MetricType.VEGA_AI] !== 'number') {
          set(errors, `limits.${MetricType.VEGA_AI}`, 'Number of Vega AI Prompts is required');
        }
      }

      if (isPlanWithAIRuns(values.planType)) {
        if (enableAIRuns && typeof values.limits[MetricType.AI_RUNS] !== 'number') {
          set(errors, `limits.${MetricType.AI_RUNS}`, 'Number of AI Runs is required');
        }
      }

      if (
        enableExperimentationKeys &&
        enableEventsReceived &&
        values.limits[MetricType.EVENTS_RECEIVED] > 0 &&
        values.limits[MetricType.EXPERIMENTATION_KEYS] > 0
      ) {
        set(
          errors,
          `limits.${MetricType.EVENTS_RECEIVED}`,
          'Number of events received and Experimentation keys cannot both be positive',
        );
      }
      if (
        enableExperimentationMAU &&
        enableExperimentationKeys &&
        values.limits[MetricType.EXPERIMENTATION_MAU] > 0 &&
        values.limits[MetricType.EXPERIMENTATION_KEYS] > 0
      ) {
        set(
          errors,
          `limits.${MetricType.EXPERIMENTATION_KEYS}`,
          'Number of Experimentation keys and Experimentation MAU cannot both be positive',
        );
      }
      if (
        enableExperimentationMAU &&
        enableEventsReceived &&
        values.limits[MetricType.EXPERIMENTATION_MAU] > 0 &&
        values.limits[MetricType.EVENTS_RECEIVED] > 0
      ) {
        set(
          errors,
          `limits.${MetricType.EVENTS_RECEIVED}`,
          'Number of events received and Experimentation MAU cannot both be positive',
        );
      }
    }

    if (values.planType === PlanType.LEGACY) {
      set(errors, 'planType', 'You may not switch to or update a legacy plan');
    }

    return errors;
  };

  public handleSubmit = (values: Plan) => {
    const {
      enableEventsReceived,
      enableEventsPublished,
      enableExperimentationKeys,
      enableExperimentationMAU,
      enableAIServiceConnections,
      enableAnalyticsMAU,
      enableErrors,
      enableLogs,
      enableSessions,
      enableTraces,
      enableAIRuns,
      enableVegaAI,
    } = this.state;
    const payload = clone(values);

    // Only send a plan version for plan types that support it and when one was
    // actually selected (defaults to no version).
    //
    // Omitting `version` here is safe and never clears the account's version. The
    // PUT /private/billingv2/accounts/{id}/subscription handler
    // (internal/private/put_account_subscription.go) treats a missing version as
    // "inherit or resolve a default", not "clear":
    //   1. Same plan type with a stored version -> inherits the current DBPlanVersion
    //      (so editing other fields without touching the dropdown preserves the version).
    //   2. Plan-type change -> ResolveBillingPlanV2 picks a flag-driven default for the new type.
    //   3. Legacy account (DBPlanVersion == 0) -> deliberately not pinned to v1; flags elevate it.
    if (!supportsPlanVersionSelection(payload.planType) || payload.version === undefined) {
      unset(payload, 'version');
    }

    if (this.isMonthlyFoundation2023(payload)) {
      set(payload, 'limits', {});

      if (!isEnterprisePlan(payload.planType) && !isGuardianPlan(payload.planType) && !payload.free) {
        unset(payload, 'effectiveStartDate');
      }

      if (payload.free) {
        unset(payload, 'prorationTime');
      }

      this.props.onSubmit(payload);
      this.setState({ isConfirmationOpen: false });
      return;
    }

    if (!enableEventsReceived) {
      set(payload, `limits.${MetricType.EVENTS_RECEIVED}`, 0);
    }
    if (!enableEventsPublished) {
      set(payload, `limits.${MetricType.EVENTS_PUBLISHED}`, 0);
    }

    if (!enableExperimentationKeys) {
      set(payload, `limits.${MetricType.EXPERIMENTATION_KEYS}`, 0);
    }

    if (!enableExperimentationMAU) {
      set(payload, `limits.${MetricType.EXPERIMENTATION_MAU}`, 0);
    }

    if (!enableAIServiceConnections) {
      set(payload, `limits.${MetricType.AI_SERVICE_CONNECTIONS}`, 0);
    }

    if (!enableAnalyticsMAU) {
      set(payload, `limits.${MetricType.ANALYTICS_MAU}`, 0);
    }

    if (!enableErrors) {
      set(payload, `limits.${MetricType.ERRORS}`, 0);
    }

    if (!enableLogs) {
      set(payload, `limits.${MetricType.LOGS}`, 0);
    }

    if (!enableSessions) {
      set(payload, `limits.${MetricType.SESSIONS}`, 0);
    }

    if (!enableTraces) {
      set(payload, `limits.${MetricType.TRACES}`, 0);
    }

    if (!enableAIRuns) {
      set(payload, `limits.${MetricType.AI_RUNS}`, 0);
    }

    if (!enableVegaAI) {
      set(payload, `limits.${MetricType.VEGA_AI}`, 0);
    }

    if (!isEnterprisePlan(payload.planType) && !isGuardianPlan(payload.planType) && !payload.free) {
      unset(payload, 'effectiveStartDate');
    }

    if (!isSelfServePlan(payload.planType)) {
      unset(payload, 'free');
      unset(payload, 'prorationTime');
    }

    if (!isPlanWithSSO(payload.planType)) {
      unset(payload, `limits.${MetricType.SSO}`);
    }

    if (payload.free) {
      unset(payload, 'prorationTime');
    }

    const isInvoiceOnlyPlan = isEnterprisePlan(payload.planType) || isGuardianPlan(payload.planType);

    if (isHostBasedPlan(payload.planType)) {
      // Invoice-only plans require all limits to be present, set to 0 if not applicable
      if (isInvoiceOnlyPlan) {
        set(payload, `limits.${MetricType.SEATS}`, 0);
      } else {
        unset(payload, `limits.${MetricType.SEATS}`);
      }
    } else if (isInvoiceOnlyPlan) {
      // Non-host-based invoice-only plans should have Hosts set to 0
      set(payload, `limits.${MetricType.HOSTS}`, 0);
    } else {
      // Self-serve non-host-based plans should not have Hosts
      unset(payload, `limits.${MetricType.HOSTS}`);
    }

    // Remove SERVER_CONNECTIONS if the setting is enabled OR for host-based plans
    if (this.props.settings.removeServerConnections || isHostBasedPlan(payload.planType)) {
      unset(payload, `limits.${MetricType.SERVER_CONNECTIONS}`);
    }

    if (!isPlanWithObservabilityProducts(payload.planType)) {
      unset(payload, `limits.${MetricType.AI_SERVICE_CONNECTIONS}`);
      unset(payload, `limits.${MetricType.ANALYTICS_MAU}`);
      unset(payload, `limits.${MetricType.ERRORS}`);
      unset(payload, `limits.${MetricType.LOGS}`);
      unset(payload, `limits.${MetricType.SESSIONS}`);
      unset(payload, `limits.${MetricType.TRACES}`);
    }

    if (!isPlanWithVegaAI(payload.planType)) {
      unset(payload, `limits.${MetricType.VEGA_AI}`);
    }

    // Remove ExperimentationMAU for plans that don't support it (Professional, Starter, etc.)
    if (!isPlanWithExperimentationMAU(payload.planType)) {
      unset(payload, `limits.${MetricType.EXPERIMENTATION_MAU}`);
    }

    if (!isPlanWithAIRuns(payload.planType)) {
      unset(payload, `limits.${MetricType.AI_RUNS}`);
    }

    if (isFixedPlan(payload.planType)) {
      unset(payload, 'limits');
    }

    this.props.onSubmit(payload);

    this.setState({ isConfirmationOpen: false });
  };

  private isMonthlyFoundation2023 = (values: Plan): boolean =>
    values.planType === PlanType.FOUNDATION_2023 && values.billingInterval === BillingInterval.MONTHLY_BILLING;

  private getNumericInputValue(val: number): number | undefined {
    if (val === UNLIMITED) {
      return undefined; // Let NumericInput show placeholder instead of "Unlimited"
    }
    return val;
  }

  private hasError = (form: FormikProps<Plan>, name: string) =>
    get(form.touched, name, false) && get(form.errors, name, null);

  private getFieldIntent = (form: FormikProps<Plan>, name: string) =>
    this.hasError(form, name) ? Intent.DANGER : Intent.NONE;

  private getFieldHelperText = (form: FormikProps<Plan>, name: string) =>
    this.hasError(form, name) ? get(form.errors, name) : undefined;
}
